import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { SHADOWS } from '../../styles/UISystem';
import {
  getMyLiveLocation,
  formatNeighborhoodDisplay,
  freshNeighborhood,
} from '../../services/location/liveLocationService';
import { hasLocationPermission, getLocationWithVenue } from '../../services/location';

/**
 * ShareLocationSheet — confirm starting/stopping live-location sharing.
 *
 * Bottom sheet modeled on QuickAddOverlay's Framer-Motion pattern (NOT the dead
 * BottomSheet primitive). Previews the user's own neighborhood — even before
 * they've started sharing — from the cached user_locations row, falling back to
 * a silent reverse-geocode when permission is already granted.
 *
 * @param {boolean} isSharing - current sharing state (drives the button label)
 * @param {function} onConfirm - called when Start/Stop is tapped
 * @param {function} onClose - called on backdrop tap
 * @param {string} [error] - inline error shown when an enable attempt fails (sheet stays open)
 * @param {boolean} [submitting] - disables the button + shows "Starting…" while enabling
 * @param {object} [initialLocation] - the stored user_locations row (from the People
 *   hydrate) used to paint the neighbourhood on first render, so the sheet never
 *   opens "Stop sharing" with a blank place; freshness-gated like the async preview.
 */
const ShareLocationSheet = ({
  isSharing = false,
  onConfirm,
  onClose,
  error = null,
  submitting = false,
  initialLocation = null,
}) => {
  // Seed the pill from the already-fetched stored row so a lit pin shows the
  // place immediately (no blank flash). loadPreview below still runs to confirm/
  // refresh, and it only ever sets a truthy value — so it upgrades this seed,
  // never blanks it.
  const [neighborhood, setNeighborhood] = useState(() => freshNeighborhood(initialLocation));

  // Body scroll lock (match QuickAddOverlay)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Neighborhood preview — cached row first, then a silent reverse-geocode if
  // permission is already granted (never prompts from the sheet).
  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      const profileUserId = localStorage.getItem('profile_user_id');
      if (profileUserId) {
        // Throws on DB error — preview is best-effort, fall through to the
        // permission-gated geocode below (same path as a missing row).
        let mine = null;
        try {
          mine = await getMyLiveLocation(profileUserId);
        } catch (err) {
          console.error('[SHARE_SHEET] Own-location preview failed:', err?.message);
        }
        // Still fresh? Use the stored neighbourhood; a stale row previews a
        // days-old place, so fall through to a live geocode instead. Same window
        // + helper as the first-paint seed above (24h since migration 047).
        const cached = freshNeighborhood(mine);
        if (cached) {
          if (!cancelled) setNeighborhood(cached);
          return;
        }
      }
      // No cached row — only geocode if permission is already granted
      if (await hasLocationPermission()) {
        try {
          const { venueInfo } = await getLocationWithVenue();
          const live = formatNeighborhoodDisplay(
            venueInfo?.address?.neighborhood,
            venueInfo?.address?.city
          );
          if (live && !cancelled) setNeighborhood(live);
        } catch (e) {
          /* preview is best-effort */
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      <motion.div
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '80dvh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
          }}
        >
          {/* Headline — lead with the action; payoff as the supporting line */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#000' }}>
              Share neighborhood
            </div>
            <div style={{ fontSize: '15px', color: '#6A6F7A', marginTop: '4px' }}>
              See where your friends are
            </div>
          </div>

          {/* Neighborhood pill (green) */}
          {neighborhood && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '20px',
                backgroundColor: 'rgba(76, 175, 80, 0.12)',
                color: '#2E7D32',
              }}
            >
              <MapPin size={16} weight="regular" color="#4CAF50" />
              <span style={{ fontSize: '14px', fontWeight: 400 }}>{neighborhood}</span>
            </div>
          )}

          {/* Reassurance — title + pill already say "neighborhood"; this adds the time bound.
              "Shows for" not "Off in": an iOS PWA can't track in the background, so this is a
              visible value with a 24h TTL, not a persistent background "on" state. */}
          <div style={{ fontSize: '15px', color: '#6A6F7A', textAlign: 'center' }}>
            Shows for 24 hours
          </div>

          {/* Inline error — enable failed (GPS timeout/no data); sheet stays open to retry */}
          {error && (
            <div style={{ fontSize: '14px', color: '#D92D20', textAlign: 'center' }}>{error}</div>
          )}

          {/* Confirm */}
          <div style={{ width: '100%', marginTop: '8px' }}>
            <ButtonRounded
              text={submitting ? 'Starting…' : isSharing ? 'Stop sharing' : 'Start sharing'}
              onClick={onConfirm}
              size="full"
              variant={isSharing ? 'secondary' : 'primary'}
              disabled={submitting}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ShareLocationSheet;
